import React from "react";
import { Link } from "react-router-dom";
import { Rocket, Check, X } from "lucide-react";
import { Chip, Loading, Empty, SectionHeader, LoadError, usePlatformFetch } from "./_primitives";

const stageColor = {
  stuck: "red",
  started: "amber",
  in_progress: "amber",
  near_complete: "ok",
  completed: "green",
};

export default function OnboardingCommand() {
  const { data, error, retry } = usePlatformFetch("/platform/onboarding_progress");
  const rows = data || [];

  if (error) return <LoadError error={error} retry={retry} label="Onboarding" />;
  if (!data) return <Loading />;
  const summary = rows.reduce((acc, r) => {
    acc.total += 1;
    acc[r.stage] = (acc[r.stage] || 0) + 1;
    acc.avg_pct += r.completion_pct;
    return acc;
  }, { total: 0, avg_pct: 0 });
  const avg = summary.total ? Math.round(summary.avg_pct / summary.total) : 0;

  return (
    <div className="space-y-4" data-testid="onboarding-command">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="fop-surface-2 rounded-lg p-3 border border-[var(--fop-border)]" data-testid="onb-stat-total">
          <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-[var(--fop-text-3)]">Active tenants</div>
          <div className="font-display text-2xl text-[var(--fop-text)] mt-0.5">{summary.total}</div>
        </div>
        <div className="fop-surface-2 rounded-lg p-3 border border-red-500/30 bg-red-500/5" data-testid="onb-stat-stuck">
          <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-[var(--fop-text-3)]">Stuck (&lt;14%)</div>
          <div className="font-display text-2xl text-red-300 mt-0.5">{summary.stuck || 0}</div>
        </div>
        <div className="fop-surface-2 rounded-lg p-3 border border-amber-500/30 bg-amber-500/5" data-testid="onb-stat-started">
          <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-[var(--fop-text-3)]">Started · 14-39%</div>
          <div className="font-display text-2xl text-amber-300 mt-0.5">{summary.started || 0}</div>
        </div>
        <div className="fop-surface-2 rounded-lg p-3 border border-blue-500/30 bg-blue-500/5" data-testid="onb-stat-in-progress">
          <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-[var(--fop-text-3)]">In progress · 40-69%</div>
          <div className="font-display text-2xl text-blue-300 mt-0.5">{(summary.in_progress || 0) + (summary.near_complete || 0)}</div>
        </div>
        <div className="fop-surface-2 rounded-lg p-3 border border-emerald-500/30 bg-emerald-500/5" data-testid="onb-stat-complete">
          <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-[var(--fop-text-3)]">Completed</div>
          <div className="font-display text-2xl text-emerald-300 mt-0.5">{summary.completed || 0}</div>
          <div className="text-[10px] font-mono text-[var(--fop-text-4)] mt-0.5">avg {avg}%</div>
        </div>
      </div>

      <div className="fop-surface-2 rounded-lg overflow-hidden" data-testid="onboarding-list">
        <SectionHeader icon={<Rocket size={11} />} title="Per-tenant onboarding progress (stuck first)" />
        {rows.length === 0 ? <Empty>No active tenants.</Empty> : (
          <table className="w-full text-xs">
            <thead className="bg-[var(--fop-surface)] text-[var(--fop-text-3)]">
              <tr>
                <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-mono">Tenant</th>
                <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-mono">Stage</th>
                <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-mono">Progress</th>
                <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-mono">Owner</th>
                <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-mono">Steps</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.tenant_id} className="border-t border-[var(--fop-border)]" data-testid={`onboarding-row-${r.tenant_id}`}>
                  <td className="px-3 py-2">
                    <Link to={`/platform/tenants/${r.tenant_id}`} className="hover:text-[var(--fop-primary)] font-display text-sm">{r.tenant_name}</Link>
                    <div className="text-[10px] font-mono text-[var(--fop-text-4)]">{r.slug}</div>
                  </td>
                  <td className="px-3 py-2"><Chip state={stageColor[r.stage] || "amber"} label={r.stage} /></td>
                  <td className="px-3 py-2 w-[180px]">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-[var(--fop-surface)] rounded-full overflow-hidden">
                        <div className={`h-full ${r.completion_pct >= 90 ? "bg-emerald-400" : r.completion_pct >= 50 ? "bg-amber-400" : "bg-red-400"}`} style={{ width: `${r.completion_pct}%` }} />
                      </div>
                      <span className="font-mono text-[10px] text-[var(--fop-text-2)]">{r.completion_pct}%</span>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-[10px] font-mono text-[var(--fop-text-3)]">{r.owner_email || "—"}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1 flex-wrap" data-testid={`onb-steps-${r.tenant_id}`}>
                      {r.steps.map((s) => (
                        <span key={s.key} title={s.label} className={`inline-flex items-center justify-center w-5 h-5 rounded ${s.ok ? "bg-emerald-500/15 text-emerald-300" : "bg-zinc-700/30 text-zinc-500"}`}>
                          {s.ok ? <Check size={11} /> : <X size={11} />}
                        </span>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
